/**
 * ResearchTab - Tab component for displaying research history and results.
 */

import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, RefreshCw01, SearchMd } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useProposalSidebar } from '@/contexts/ActionSidebarContext';
import { BadgeWithDot } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { apiPatch } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import { quickEmailProposal } from '@/lib/proposals/core/ActionBuilder';
import { useCreateProposal } from '@/lib/proposals/query/mutations';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { SectionCard } from '../components/SectionCard';
import { canResearch, ResearchButton } from './ResearchButton';
import { ResearchDetail } from './ResearchDetail';
import { ResearchListItem } from './ResearchListItem';
import { ResearchNeedsMoreInfo } from './ResearchNeedsMoreInfo';
import { getResearchOutcome, getResearchStatusBadge } from './researchOutcome';
import type { Research } from './useResearch';
import { useResearch } from './useResearch';

interface ResearchTabProps {
  /** Organization ID */
  orgId: number;
  /** Entity ID of the record */
  entityId: string;
  /** Record type: 'contact' or 'company' */
  recordType: 'contact' | 'company';
  /** The CRM object (contact or company) to check for research eligibility */
  object: CrmObject;
}

/**
 * Error state component.
 */
function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <SectionCard title='Research' scrollable>
      <div className='flex flex-col items-center justify-center py-12 text-center'>
        <p className='text-sm text-utility-error-600 mb-4'>
          We couldn&apos;t load research
        </p>
        <Button
          color='secondary'
          size='sm'
          onClick={onRetry}
          iconLeading={RefreshCw01}
        >
          Retry
        </Button>
      </div>
    </SectionCard>
  );
}

/**
 * Loading skeleton for research detail.
 */
function LoadingSkeleton() {
  return (
    <SectionCard
      title='Research'
      scrollable
      actions={
        <div className='h-8 w-24 bg-bg-tertiary rounded-lg animate-pulse' />
      }
    >
      <div className='animate-pulse space-y-4'>
        {/* Status bar skeleton */}
        <div className='flex items-center justify-between'>
          <div className='h-5 bg-bg-tertiary rounded w-20' />
          <div className='h-3 bg-bg-tertiary rounded w-16' />
        </div>
        {/* Content skeleton */}
        <div className='space-y-3'>
          <div className='h-4 bg-bg-tertiary rounded w-full' />
          <div className='h-4 bg-bg-tertiary rounded w-3/4' />
          <div className='h-4 bg-bg-tertiary rounded w-1/2' />
        </div>
      </div>
    </SectionCard>
  );
}

/**
 * Research tab for CRM object profile page.
 * Displays research history with expandable detail view.
 */
export function ResearchTab({
  orgId,
  entityId,
  recordType,
  object,
}: ResearchTabProps) {
  // State for history accordion and selected history item
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { research, isLoading, error, refetch, hasInProgressResearch } =
    useResearch({
      orgId,
      entityId,
      recordType,
    });

  // Split research into most recent and history
  const mostRecent = research[0] ?? null;
  const history = research.slice(1);

  // Determine which research to display (selected history item or most recent)
  const displayedResearch = selectedId
    ? (research.find(r => r.entity_id === selectedId) ?? mostRecent)
    : mostRecent;

  // Auto-switch to newest research when a new in-progress one appears
  useEffect(() => {
    if (hasInProgressResearch && mostRecent?.status === 'in_progress') {
      // If viewing history but new research started, switch to it
      if (selectedId && selectedId !== mostRecent.entity_id) {
        setSelectedId(null);
      }
    }
  }, [
    hasInProgressResearch,
    mostRecent?.entity_id,
    mostRecent?.status,
    selectedId,
  ]);

  const { user } = useAuthInfo();
  const createProposal = useCreateProposal(orgId);
  const { openProposal } = useProposalSidebar();
  const queryClient = useQueryClient();
  const contactEmailRaw = getPropertyValue<string | string[]>(
    object.properties?.ahoy_email
  );
  const contactEmail = Array.isArray(contactEmailRaw)
    ? contactEmailRaw[0]
    : contactEmailRaw;

  /**
   * Update the React Query cache to set proposal_id on a component in a research item's
   * view_definition_json. This provides instant UI feedback (button changes to "Open Draft")
   * without waiting for a refetch.
   */
  const updateViewDefinitionCache = useCallback(
    (researchEntityId: string, componentId: string, proposalId: number) => {
      const queryKey = ['research', orgId, entityId, recordType];
      queryClient.setQueryData<Research[]>(queryKey, oldData => {
        if (!oldData) {
          return oldData;
        }
        return oldData.map(item => {
          if (item.entity_id !== researchEntityId) {
            return item;
          }
          if (!item.view_definition_json) {
            return item;
          }

          try {
            const viewDef = JSON.parse(item.view_definition_json);
            // Recursively find and update the component (may be nested in children)
            type ComponentNode = {
              id: string;
              props?: Record<string, unknown>;
              children?: ComponentNode[];
            };
            const updateComponent = (components: ComponentNode[]): boolean => {
              for (const comp of components) {
                if (comp.id === componentId) {
                  comp.props = { ...comp.props, proposal_id: proposalId };
                  return true;
                }
                if (comp.children && updateComponent(comp.children)) {
                  return true;
                }
              }
              return false;
            };
            updateComponent(viewDef.components || []);
            return {
              ...item,
              view_definition_json: JSON.stringify(viewDef),
            };
          } catch {
            return item;
          }
        });
      });
    },
    [orgId, entityId, recordType, queryClient]
  );

  const handleComposeEmail = useCallback(
    async (data: {
      component_id: string;
      subject: string;
      body: string;
      context_label: string;
    }) => {
      if (!contactEmail || !user?.id) {
        return;
      }

      // Client-side validation before mutation
      if (!data.subject?.trim() || !data.body?.trim()) {
        toast.error('Conversation starter is missing subject or body content');
        return;
      }

      // Use the currently displayed research item (could be selectedId or mostRecent)
      const researchEntityId = displayedResearch?.entity_id;
      if (!researchEntityId) {
        return;
      }

      try {
        const request = quickEmailProposal({
          title: `Email: ${data.subject}`,
          description: `Conversation starter from research: ${data.context_label}`,
          assignedUserId: user.id,
          sourceTypeName: 'ahoy_contact_research',
          sourceId: entityId,
          sourceContext: {
            channel: 'ahoy_research',
            account_identifier: user.email,
          },
          to: [contactEmail],
          subject: data.subject,
          body: data.body,
        });
        const proposal = await createProposal.mutateAsync(request);
        openProposal(proposal.id);

        // Store proposal_id on the component via backend PATCH
        // This persists the link so the button shows "Open Draft" after refresh
        try {
          await apiPatch(
            `/api/orgs/${orgId}/research/${researchEntityId}/components/${data.component_id}/`,
            { proposal_id: proposal.id }
          );
        } catch (patchError) {
          // Non-critical: log but don't fail the flow — the proposal was already created
          logger.error(
            'Failed to store proposal_id on conversation starter component',
            patchError instanceof Error ? patchError : undefined,
            {
              entity_id: researchEntityId,
              component_id: data.component_id,
              proposal_id: proposal.id,
            }
          );
        }

        // Optimistically update cache so the button changes to "Open Draft" immediately
        updateViewDefinitionCache(
          researchEntityId,
          data.component_id,
          proposal.id
        );
      } catch (error) {
        logger.error(
          'Failed to create email proposal from conversation starter',
          error instanceof Error ? error : undefined,
          {
            entity_id: entityId,
            context_label: data.context_label,
            has_email: !!contactEmail,
          }
        );
        toast.error("We couldn't create the email draft. Try again.");
      }
    },
    [
      contactEmail,
      user,
      entityId,
      displayedResearch?.entity_id,
      orgId,
      createProposal,
      openProposal,
      updateViewDefinitionCache,
    ]
  );

  const handleOpenProposal = useCallback(
    (proposalId: number) => {
      openProposal(proposalId);
    },
    [openProposal]
  );

  const isResearchable = canResearch(object, recordType);

  // Callback when research is created - just refetch the list
  const handleResearchCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  // Handler for selecting a history item
  const handleSelectHistoryItem = useCallback((item: Research) => {
    setSelectedId(item.entity_id);
  }, []);

  // Handler for going back to latest
  const handleBackToLatest = useCallback(() => {
    setSelectedId(null);
  }, []);

  // Check if we're viewing a history item (not the most recent)
  const isViewingHistory = selectedId && selectedId !== mostRecent?.entity_id;

  const statusBadge = useMemo(
    () =>
      getResearchStatusBadge(
        displayedResearch ? getResearchOutcome(displayedResearch) : 'queued'
      ),
    [displayedResearch]
  );

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return <ErrorState onRetry={refetch} />;
  }

  // Empty state
  if (research.length === 0) {
    if (!isResearchable) {
      return (
        <SectionCard title='Research' scrollable>
          <ResearchNeedsMoreInfo recordType={recordType} />
        </SectionCard>
      );
    }

    return (
      <SectionCard
        title='Research'
        scrollable
        actions={
          <ResearchButton
            orgId={orgId}
            entityId={entityId}
            recordType={recordType}
            object={object}
            latestResearch={null}
            onResearchCreated={handleResearchCreated}
          />
        }
      >
        <EmptyState
          variant='empty'
          title='No research yet'
          description='Generate research to learn more about this record.'
          icon={
            <FeaturedIcon
              icon={SearchMd}
              size='md'
              theme='light-outline'
              color='gray'
            />
          }
          size='sm'
        />
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title='Research'
      scrollable
      actions={
        <ResearchButton
          orgId={orgId}
          entityId={entityId}
          recordType={recordType}
          object={object}
          latestResearch={null}
          onResearchCreated={handleResearchCreated}
        />
      }
    >
      <div className='space-y-4'>
        {/* Status bar: badge + timestamp + back link */}
        {displayedResearch && (
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <BadgeWithDot color={statusBadge.color} size='sm'>
                {statusBadge.label}
              </BadgeWithDot>
              {isViewingHistory && (
                <button
                  onClick={handleBackToLatest}
                  className='text-xs text-utility-brand-600 dark:text-utility-brand-400 hover:underline'
                >
                  ← Back to latest
                </button>
              )}
            </div>
            {displayedResearch.created_at && (
              <span className='text-xs text-tertiary'>
                {formatRelativeTime(displayedResearch.created_at)}
              </span>
            )}
          </div>
        )}

        {/* Research detail content - no wrapper, flows directly */}
        {displayedResearch && (
          <ResearchDetail
            research={displayedResearch}
            object={object}
            onComposeEmail={contactEmail ? handleComposeEmail : undefined}
            onOpenProposal={handleOpenProposal}
            isComposing={createProposal.isPending}
          />
        )}

        {/* Accordion: Previous Research */}
        {history.length > 0 && (
          <div>
            <button
              onClick={() => setHistoryOpen(!historyOpen)}
              className='flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary'
            >
              <ChevronRight
                className={cn(
                  'size-4 transition-transform',
                  historyOpen && 'rotate-90'
                )}
              />
              Previous research ({history.length})
            </button>

            {historyOpen && (
              <div className='mt-2 space-y-2 pl-6'>
                {history.map(item => (
                  <ResearchListItem
                    key={item.entity_id}
                    research={item}
                    isSelected={item.entity_id === selectedId}
                    onClick={() => handleSelectHistoryItem(item)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
}
